import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Skeleton } from '../../design-system';
import {
  askAi, getAiSettings, updateAiSettings,
  listConversations, getConversation, deleteConversation,
} from '../../lib/command';
import './CommandBar.css';

// The conversational tab.
//
// ⚠️ IT ANSWERS FROM SEARCH, NOT FROM MEMORY. The server runs the question
// through the same permission-filtered search the Search tab uses and hands the
// model those records to answer from. So it can say what is pending for a
// client, and it cannot invent one — and it can never mention a record the
// asker could not already open themselves.
//
// ⚠️ IT WRITES NOTHING. Creating work goes through the Command tab, which shows
// a confirmation first. That is stated on screen rather than left implicit,
// because a chat box that looks like it might act is one people will try to
// make act.
//
// ⚠️ THE THREAD LIVES ON THE SERVER, NOT IN THIS COMPONENT'S STATE. It used to
// be a `useState([])`, so every conversation was lost on a refresh and the
// history sent to the model was whatever the browser still had — which is both
// the bug this fixes and the reason the model's context is now read server-side
// from the stored turns. What is kept locally is one id in `localStorage`:
// which thread this browser had open, so a reload lands back where it was.
// Everything the id points at is re-fetched and re-checked against the caller.

// Why the tier is off, said in the user's terms rather than the server's.
//
// ⚠️ THE COPY LIVES HERE AND THE STORED ROW HOLDS ONLY THE CODE, so a notice
// reloaded out of history reads exactly as it did when it arrived, and
// improving the wording does not leave old history phrased the old way.
const REASON_COPY = {
  disabled: 'AI replies are switched off for your firm.',
  unconfigured: 'No AI provider is configured on the server yet.',
  kill_switch: 'AI replies are switched off across the platform.',
  cap_reached: 'This firm has used its monthly AI allowance.',
  rate_limited: 'The AI provider is busy — try again in a minute.',
  circuit_open: 'The AI provider is not responding — try again shortly.',
  unavailable: 'The AI provider could not be reached.',
  empty: 'Type a question first.',
};

// Which thread this browser had open. Per-viewer convenience, the one thing
// that is genuinely local — the conversation itself is the server's.
const LAST_THREAD_KEY = 'ai.ask.lastConversationId';
const readLastThread = () => {
  try { return window.localStorage.getItem(LAST_THREAD_KEY) || null; } catch { return null; }
};
const writeLastThread = (id) => {
  try {
    if (id) window.localStorage.setItem(LAST_THREAD_KEY, id);
    else window.localStorage.removeItem(LAST_THREAD_KEY);
  } catch { /* private window, or site data blocked — the thread still loads */ }
};

const dayLabel = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

export default function AskAi() {
  const [turns, setTurns] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [threads, setThreads] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showThreads, setShowThreads] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [unsaved, setUnsaved] = useState(false);
  const endRef = useRef(null);
  const threadRef = useRef(null);

  const refreshThreads = useCallback(
    () => listConversations().then(setThreads).catch(() => {}),
    []
  );

  const openThread = useCallback(async (id) => {
    if (!id) {
      setConversationId(null); setTurns([]); setHasMore(false); writeLastThread(null);
      return;
    }
    setLoadingThread(true);
    try {
      const data = await getConversation(id);
      setConversationId(data.conversation.id);
      setTurns(data.messages);
      setHasMore(data.hasMore);
      writeLastThread(data.conversation.id);
    } catch {
      // A thread this browser remembers but the server will not return — it was
      // deleted, or this is a different login. Start clean rather than showing
      // an error about something the user did not ask for.
      setConversationId(null); setTurns([]); setHasMore(false); writeLastThread(null);
    } finally {
      setLoadingThread(false);
    }
  }, []);

  useEffect(() => { getAiSettings().then(setSettings).catch(() => {}); }, []);

  // Restore on mount: the thread this browser had open, or the most recent one.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await listConversations().catch(() => []);
      if (cancelled) return;
      setThreads(list);
      const remembered = readLastThread();
      const target = list.some((t) => t.id === remembered) ? remembered : list[0]?.id;
      if (target) openThread(target);
    })();
    return () => { cancelled = true; };
  }, [openThread]);

  // ⚠️ KEYED ON THE LAST TURN, NOT ON `turns`. Scrolling to the bottom on every
  // change would yank the reader down the moment older history is paged in
  // above, which makes reading a long thread impossible — the same rule the
  // client-chat scroll-back loader follows.
  const lastId = turns.length ? (turns[turns.length - 1].id || turns.length) : null;
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [lastId]);

  /** Page backwards. The scroll position is restored so the prepend is invisible. */
  const loadOlder = async () => {
    if (!conversationId || loadingMore || !turns.length) return;
    setLoadingMore(true);
    const box = threadRef.current;
    const before = box?.scrollHeight ?? 0;
    try {
      const data = await getConversation(conversationId, { before: turns[0].id });
      setTurns((t) => [...data.messages, ...t]);
      setHasMore(data.hasMore);
      requestAnimationFrame(() => {
        if (box) box.scrollTop += box.scrollHeight - before;
      });
    } catch { /* leave what is on screen; the button can be pressed again */ } finally {
      setLoadingMore(false);
    }
  };

  const send = async () => {
    const q = text.trim();
    if (!q || busy) return;
    setText('');
    // Optimistic, with no id — the server's row id replaces it on the response.
    setTurns((t) => [...t, { role: 'user', content: q, at: new Date().toISOString() }]);
    setBusy(true);
    try {
      const res = await askAi({ text: q, conversationId });
      if (res.conversationId && res.conversationId !== conversationId) {
        setConversationId(res.conversationId);
        writeLastThread(res.conversationId);
      }
      // The answer arrived but the server could not file it. Said out loud
      // rather than left to be discovered on the next refresh, when the turn
      // would simply be missing.
      setUnsaved(!res.persisted);
      setTurns((t) => [...t, res.error
        ? { id: res.messageId, role: 'assistant', isNotice: true, noticeCode: res.error, at: new Date().toISOString() }
        : {
          id: res.messageId,
          role: 'assistant',
          content: res.reply,
          grounded: res.grounded,
          sources: res.sources,
          at: new Date().toISOString(),
        }]);
      refreshThreads();
    } catch (e) {
      // A failed request — the turn was never stored, so this bubble is local
      // and will not survive a refresh. That is correct: nothing happened.
      setTurns((t) => [...t, {
        role: 'assistant', isNotice: true,
        content: e.response?.data?.error || 'Could not reach the assistant.',
        at: new Date().toISOString(),
      }]);
    } finally {
      setBusy(false);
    }
  };

  const removeThread = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this conversation? The history will no longer be shown.')) return;
    try {
      await deleteConversation(id);
      const rest = threads.filter((t) => t.id !== id);
      setThreads(rest);
      if (id === conversationId) openThread(rest[0]?.id || null);
    } catch { /* the row stays listed; the server refused and the list is unchanged */ }
  };

  const enable = async () => {
    setSaving(true);
    try {
      const res = await updateAiSettings({ aiEnabled: true });
      setSettings((s) => ({ ...s, settings: res.settings }));
    } catch {
      /* the banner stays; the server refused and said why in its own response */
    } finally {
      setSaving(false);
    }
  };

  const off = settings && !settings.settings.aiEnabled;
  const unconfigured = settings && !settings.configured;
  const bodyOf = (t) => (t.isNotice
    ? (REASON_COPY[t.noticeCode] || t.content || 'That did not work.')
    : t.content);

  return (
    <div className="cmd-ask">
      {off && (
        <div className="cmd-ask-banner">
          <span>
            <b>AI replies are off for your firm.</b>{' '}
            {unconfigured
              ? 'No provider is configured on the server, so turning this on will not do anything yet.'
              : 'Search and the Command tab work without it. Turning it on sends your search results — client and task names — to the AI provider.'}
          </span>
          {settings.canWrite && !unconfigured && (
            <Button variant="primary" loading={saving} onClick={enable}>Turn on</Button>
          )}
          {!settings.canWrite && <small>A manager can switch it on.</small>}
        </div>
      )}

      {unsaved && (
        <p className="cmd-warn">
          The last reply could not be saved to your history — it will not be here after a refresh.
        </p>
      )}

      <div className="cmd-ask-bar">
        <Button
          variant="ghost"
          onClick={() => setShowThreads((v) => !v)}
          aria-expanded={showThreads}
        >
          {showThreads ? 'Hide' : 'History'}
          {threads.length ? ` (${threads.length})` : ''}
        </Button>
        <Button variant="ghost" onClick={() => openThread(null)} disabled={!turns.length && !conversationId}>
          New chat
        </Button>
      </div>

      {showThreads && (
        <ul className="cmd-ask-threads">
          {!threads.length && <li className="cmd-hint">No earlier conversations yet.</li>}
          {threads.map((t) => (
            <li key={t.id} className={`cmd-ask-thread-row${t.id === conversationId ? ' is-on' : ''}`}>
              <button type="button" className="cmd-ask-thread-open" onClick={() => openThread(t.id)}>
                <span className="cmd-ask-thread-title">{t.title}</span>
                <span className="cmd-ask-thread-meta">
                  {t.messageCount} message{t.messageCount === 1 ? '' : 's'} · {dayLabel(t.lastMessageAt)}
                </span>
              </button>
              <button
                type="button"
                className="cmd-ask-thread-del"
                aria-label={`Delete conversation ${t.title}`}
                onClick={() => removeThread(t.id)}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="cmd-ask-thread" ref={threadRef}>
        {hasMore && (
          <div className="cmd-ask-older">
            <Button variant="ghost" loading={loadingMore} onClick={loadOlder}>
              Load earlier messages
            </Button>
          </div>
        )}
        {loadingThread && <Skeleton.Text lines={3} />}
        {!loadingThread && !turns.length && (
          <p className="cmd-hint">
            Ask something like “what is pending for Acme?” or just say hello.
            The assistant can look things up; it cannot create or change anything.
            Your conversations are kept, and only you can read them.
          </p>
        )}
        {turns.map((t, i) => (
          <div
            key={t.id || `local-${i}`}
            className={`cmd-ask-turn is-${t.role}${t.isNotice ? ' is-notice' : ''}`}
          >
            <div className="cmd-ask-bubble">{bodyOf(t)}</div>
            {t.grounded && !!t.sources?.length && (
              // Shown so the answer is checkable: these are the record types it
              // was allowed to read, not a claim about what exists.
              <small className="cmd-ask-sources">from your {t.sources.join(', ').toLowerCase()}</small>
            )}
          </div>
        ))}
        {busy && <Skeleton.Text lines={2} />}
        <div ref={endRef} />
      </div>

      <div className="cmd-text">
        <input
          className="cmd-text-input"
          value={text}
          disabled={busy}
          placeholder="Ask a question…"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
        />
        <Button variant="primary" onClick={send} loading={busy} disabled={!text.trim()}>Ask</Button>
      </div>
    </div>
  );
}
